import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { dataGridPremiumColumns, generateColumnsFromDynProps } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi } from '../Interface';
import taskFeedbackService from '../../service/taskFeedback.service';
import useParam from '../../context/ParamContext';
import { getProjectOrderNumberName } from '../../utils/getProjectOrderNumberName';

const TaskFeedbackReports = ({ taskFeedbackState }) => {
  const {
    setRefetchTable,
    setSelectedValues,
    viewField,
    operatorsDisabled,
    refetchTable,
    autocompleteOptions,
    rowSelectionModel,
    setRowSelectionModel,
    dynProps,
  } = taskFeedbackState;
  const { params } = useParam();
  const [reports, setReports] = useState({ rows: [], rowCount: 0 });

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getList = (gridState) => {
    taskFeedbackService.getRows(gridState).then((data) => {
      setReports(data);
    });
  };

  const columns = useMemo(() => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Rövid visszajelzés',
        type: 'string',
      },
      {
        field: 'startDate',
        type: 'date',
        headerName: 'Kezdő dátum',
        valueGetter: (_data, row) => {
          return new Date(row.startDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.startDate).format('YYYY.MM.DD HH:mm');
        },
      },
      {
        field: 'endDate',
        type: 'date',
        headerName: 'Befejező dátum',
        valueGetter: (_data, row) => {
          return new Date(row.endDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.endDate).format('YYYY.MM.DD HH:mm');
        },
      },
      ...(params.HIDE_TASKFEEDBACK_RELEVANT_DEP_FIELD !== 'Y'
        ? [
            {
              field: 'relevantDepartmentId',
              headerName: 'Érintett részleg',
              type: 'singleSelect',
              sortable: false,
              renderCell: (params) => {
                return params.row.relevantDepartment?.name;
              },
              valueOptions: autocompleteOptions.relevantDepartment,
            },
          ]
        : []),
      {
        field: 'statusId',
        headerName: 'Státusz',
        type: 'singleSelect',
        sortable: false,
        renderCell: (params) => {
          return params.row.status?.itemName;
        },
        valueOptions: autocompleteOptions.status,
      },
      {
        field: 'typeId',
        headerName: 'Típus',
        type: 'singleSelect',
        sortable: false,
        renderCell: (params) => {
          return params.row.type?.itemName;
        },
        valueOptions: autocompleteOptions.type,
      },
      {
        field: 'priorityId',
        headerName: 'Prioritás',
        type: 'singleSelect',
        sortable: false,
        renderCell: (params) => {
          return params.row.priority?.itemName;
        },
        valueOptions: autocompleteOptions.priority,
      },
      {
        field: 'createdBy',
        headerName: 'Tulajdonos',
        type: 'singleSelect',
        sortable: false,
        renderCell: (params) => {
          return params.row.user?.userName;
        },
        valueOptions: autocompleteOptions.user,
      },
      ...(params.HIDE_TASKFEEDBACK_PROD_OPERATIONID_FIELD !== 'Y'
        ? [
            {
              field: 'prodOperationId',
              headerName: 'Gyártási utasítás szám',
              type: 'string',
              sortable: false,
              filterable: false,
              renderCell: (params) => {
                return params.row?.productionOperation?.label;
              },
            },
          ]
        : []),
      ...(params.SHOW_TASKFEEDBACK_PROJECT_FIELD === 'Y'
        ? [
            {
              field: 'projectId',
              headerName: 'Projekt',
              type: 'string',
              renderCell: (params) => {
                return params.row?.project?.projectName;
              },
            },
            {
              field: 'projectOrderNumber',
              headerName: getProjectOrderNumberName(params),
              type: 'string',
              sortable: false,
              renderCell: (data) => {
                return data.row.project?.orderNumber;
              },
            },
          ]
        : []),
      ...generateColumnsFromDynProps(dynProps ?? []),
    ]);
  }, [autocompleteOptions, dynProps]);

  return (
    <DatagridPremiumUi
      datagridIdentifier={DataGridIdentifiers.taskFeedback}
      datagridInitialState={initialState}
      dataLoader={getList}
      rows={reports.rows}
      refetch={{ refetchTable, setRefetchTable }}
      rowCount={reports.rowCount}
      columns={[...columns]}
      onRowClick={(rowData) => {
        if (!viewField) return;
        setSelectedValues(rowData.row);
      }}
      paginationAndfilterMode="server"
      onRowSelectionModelChange={(newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel);
      }}
      rowSelectionModel={rowSelectionModel}
      disableRowSelectionOnClick={!viewField}
    />
  );
};

export default TaskFeedbackReports;
